import {
  CircularProgress,
  Button as MuiButton,
  capitalize,
} from '@mui/material';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Table } from '../Default';
import ChevronDownSVG from '../../../assets/icons/chevron-down.svg';
import { ObjectWithId } from '../types';
import { Accordion } from '../Accordion';

type THSortProps = {
  col: string;
  order: string;
  orderBy: string;
  onClick: () => void;
};

export type GenericTableProps<T extends ObjectWithId> = {
  rows: T[];
  getSummary: (row: T) => string;
  selectableAccordionTitle?: string;
  isFetching?: boolean;
  isSortable?: boolean;
  isSelectable?: boolean;
  onSelectRow?: (selected: T) => void;
  AboveHeader?: JSX.Element;
  Footer?: JSX.Element;
  customSort?: Partial<Record<keyof T, (a: T, b: T) => number>>;
};

type InnerTableProps<T extends ObjectWithId> = Pick<
  GenericTableProps<T>,
  | 'rows'
  | 'isFetching'
  | 'isSortable'
  | 'isSelectable'
  | 'onSelectRow'
  | 'getSummary'
  | 'selectableAccordionTitle'
> & {
  columns: string[];
  order: 'asc' | 'desc';
  orderBy: string;
  sortBy: (col: string) => () => void;
  className?: string;
};

const THSort = ({ col, onClick, order, orderBy }: THSortProps) => (
  <Table.HCell>
    <MuiButton
      onClick={onClick}
      className={clsx(
        'group flex gap-1 py-0 px-2 w-full max-w-[150px] m-auto h-7 justify-between',
        'text-center text-black font-semibold',
        'rounded-full bg-orange-light'
      )}
    >
      <div className="w-3"></div>
      {capitalize(col)}
      <img
        src={ChevronDownSVG}
        alt="Ordenar"
        className={clsx(
          'w-3 transform duration-200',
          orderBy === col ? 'opacity-100' : 'opacity-0',
          order === 'desc' ? 'rotate-0' : 'rotate-180',
          'group-hover:opacity-100'
        )}
      />
    </MuiButton>
  </Table.HCell>
);

const BigTable = <T extends ObjectWithId>({
  columns,
  order,
  orderBy,
  rows,
  isFetching,
  isSortable,
  isSelectable,
  onSelectRow,
  sortBy,
  className,
}: InnerTableProps<T>) => {
  return (
    <Table.Root className={className} stickyHeader>
      <Table.Head>
        {!isFetching ? (
          <Table.Row>
            {columns.map((col) =>
              isSortable ? (
                <THSort
                  key={col}
                  col={col}
                  onClick={sortBy(col)}
                  order={order}
                  orderBy={orderBy}
                />
              ) : (
                <Table.HCell key={col}>{capitalize(col)}</Table.HCell>
              )
            )}
          </Table.Row>
        ) : null}
      </Table.Head>
      <Table.Body>
        {!isFetching ? (
          rows.map((row) => (
            <Table.Row
              key={row.id}
              className={clsx({
                'hover:brightness-90 hover:cursor-pointer': isSelectable,
              })}
              onClick={() => onSelectRow?.(row)}
            >
              {columns.map((col) => (
                <Table.Cell key={`${row.id}-${col}`}>{row[col]}</Table.Cell>
              ))}
            </Table.Row>
          ))
        ) : (
          <Table.Row className="flex items-center justify-center">
            <Table.Cell transparent>
              <CircularProgress size="1.66rem" color="secondary" />
            </Table.Cell>
          </Table.Row>
        )}
        {!rows.length && !isFetching && (
          <Table.Row className="flex items-center justify-center">
            <Table.Cell transparent>
              <div className="text-center">Nenhum resultado encontrado.</div>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

const AccordionTable = <T extends ObjectWithId>({
  rows,
  columns,
  getSummary,
  isFetching,
  isSelectable,
  onSelectRow,
  selectableAccordionTitle,
  className,
}: InnerTableProps<T>) => {
  const [expanded, setExpanded] = useState<number | undefined>();

  const handleExpandedChange = (index: number, expanded: boolean) => {
    setExpanded(expanded ? index : undefined);
  };

  const rowsExtended = useMemo(() => {
    if (!isSelectable) return rows;

    return rows.map((row) => ({
      ...row,
      Ações: (
        <MuiButton
          variant="contained"
          onClick={() => onSelectRow?.(row)}
          className="w-full h-full"
        >
          {selectableAccordionTitle}
        </MuiButton>
      ),
    }));
  }, [isSelectable, onSelectRow, rows, selectableAccordionTitle]);

  const columnsExtended = useMemo(() => {
    if (!isSelectable) return columns;
    return [...columns, 'Ações'];
  }, [columns, isSelectable]);

  return (
    <Accordion.Root className={className}>
      {!isFetching ? (
        rowsExtended.map((row, i) => (
          <Accordion.Row
            key={row.id}
            summary={getSummary(row)}
            expanded={i === expanded}
            onChange={(_, exp) => handleExpandedChange(i, exp)}
          >
            {columnsExtended.map((col) => (
              <Accordion.Cell key={`${row.id}-${col}`} label={capitalize(col)}>
                {row[col]}
              </Accordion.Cell>
            ))}
          </Accordion.Row>
        ))
      ) : (
        <div className="flex items-center justify-center px-4 py-3">
          <CircularProgress size="1.66rem" color="secondary" />
        </div>
      )}

      {!rows.length && !isFetching && (
        <div className="text-center p-4">Nenhum resultado encontrado.</div>
      )}
    </Accordion.Root>
  );
};

export function TableComponent<T extends ObjectWithId>(
  props: InnerTableProps<T>
) {
  return (
    <>
      <div className="hidden md:block">
        <BigTable {...props} />
      </div>

      <div className="md:hidden">
        <AccordionTable {...props} />
      </div>
    </>
  );
}

export function GenericTable<T extends ObjectWithId>({
  rows,
  getSummary,
  isSortable,
  isSelectable,
  selectableAccordionTitle = 'Ver detalhes',
  isFetching = false,
  onSelectRow,
  AboveHeader,
  Footer,
  customSort = {},
}: GenericTableProps<T>) {
  if (isSelectable && !onSelectRow)
    throw new Error(
      'You must provide an onSelectRow function when isSelectable is true'
    );

  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const columns = useMemo(() => {
    return rows.length > 0
      ? Object.keys(rows[0]).filter((key) => !key.includes('id'))
      : [];
  }, [rows]);

  const sortedRows: T[] = useMemo(() => {
    if (!orderBy || !isSortable) return rows;

    const defaultSort = (a: T, b: T) => {
      if (a[orderBy] === b[orderBy]) return 0;
      return a[orderBy] > b[orderBy] ? 1 : -1;
    };

    const sorted = rows.sort(customSort[orderBy] || defaultSort);

    return order === 'asc' ? sorted : sorted.reverse();
  }, [orderBy, isSortable, rows, customSort, order]);

  const sortBy = (col: string) => () => {
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
      return;
    }

    setOrderBy(col);
    setOrder('desc');
  };

  return (
    <div className={clsx('w-full bg-white rounded-[45px]')}>
      <div className="w-full h-min p-6">{AboveHeader}</div>
      <TableComponent
        className={clsx(
          'h-full max-h-[500px]',
          'scroll-table rounded-none overflow-auto'
        )}
        columns={columns}
        order={order}
        orderBy={orderBy}
        rows={sortedRows}
        isFetching={isFetching}
        isSortable={isSortable}
        isSelectable={isSelectable}
        onSelectRow={onSelectRow}
        sortBy={sortBy}
        getSummary={getSummary}
        selectableAccordionTitle={selectableAccordionTitle}
      />
      <div className="w-full p-6">{Footer ?? null}</div>
    </div>
  );
}
